import { useEffect, useMemo, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { Button } from "@shared/ui/button";
import { Phone, ScrollText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useCallPurpose, useCallStatus, useCreateCallEntry } from "../hooks";

interface InlineCallEntryProps {
  entityId: string | undefined;
  remoteUtilityId: string | number | undefined;
  action?: string;
  onCallDataChange?: (callData: CallEntryData) => void;
  onValidationChange?: (isValid: boolean) => void;
  onCancel?: () => void;
  showSubmitButton?: boolean; // New prop to control submit button visibility
}

export interface CallEntryData {
  consumer: number;
  call_purpose: number;
  call_status: number;
  call_date: Date;
  call_time: any;
  notes?: string;
}

export interface InlineCallEntryRef {
  submitCallEntry: () => Promise<CallEntryData>;
}

const callScripts = {
  "0": "Hello, I'm calling regarding your service request. I'd like to discuss the details of your request and provide you with an update on the status. Do you have a few minutes to talk about your service needs?",
  "1": "Hello, I'm calling to follow up on the complaint you submitted. I want to ensure we address your concerns properly and provide you with a resolution. Can we discuss the details of your complaint?",
  "2": "Hello, I'm calling regarding your transfer request. I need to verify some details and guide you through the transfer process. Do you have your identification and new address information ready?",
  "3": "Hello, I'm calling to inform you about a scheduled disconnection or to discuss your disconnection notice. We want to help you avoid any service interruption. Can we discuss your options?",
  "4": "Hello, I'm calling regarding your reconnection request. I'll need to verify that all requirements have been met and schedule your service restoration. Do you have your payment confirmation available?",
  "5": "Hello, I'm calling regarding your recent payment or payment arrangement. I'd like to confirm the details and ensure everything is processed correctly. Do you have your account information available?",
  "6": "Hello, I'm calling about your outstanding balance. I'd like to discuss payment options and help you resolve this matter. We have several payment plans available that might work for your situation.",
  "7": "Hello, I'm calling to help you set up or troubleshoot our self-service options. This will allow you to manage your account online or through our mobile app. Would you like assistance with registration?",
};

const initialFormData = {
  purpose: "",
  status: "",
  date: undefined as Date | undefined,
  time: "",
  notes: "",
};

export const InlineCallEntry = forwardRef<InlineCallEntryRef, InlineCallEntryProps>(({
  entityId,
  remoteUtilityId,
  action,
  onCallDataChange,
  onValidationChange,
  onCancel,
  showSubmitButton = true,
}, ref) => {
  const [formData, setFormData] = useState(initialFormData);
  const { data: callPurpose } = useCallPurpose();
  const { data: statusOptions } = useCallStatus();
  const { mutateAsync: createCallEntry } = useCreateCallEntry();

  const getPurposeOptions = useMemo(() => {
    if (!callPurpose || !Array.isArray(callPurpose)) {
      return [];
    }
    return callPurpose.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [callPurpose]);

  const getStatusOptions = useMemo(() => {
    if (!statusOptions || !Array.isArray(statusOptions)) {
      return [];
    }
    return statusOptions.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [statusOptions]);

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "purpose",
        type: "select",
        label: "Call Purpose",
        placeholder: "Select call purpose",
        required: true,
        options: getPurposeOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "status",
        type: "select",
        label: "Call Status",
        placeholder: "Select call status",
        required: true,
        options: getStatusOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "date",
        type: "datepicker",
        label: "Call Date",
        placeholder: "Select call date",
        required: true,
        className: "col-span-1 mt-3",
        allowPast:false,
        disabledDate: (current: Date) => {
          const today = new Date();
          // Remove time component from both dates
          today.setHours(0, 0, 0, 0);
          current.setHours(0, 0, 0, 0);
          return current < today;
        },
      },
      {
        name: "time",
        type: "time",
        label: "Call Time",
        placeholder: "Select call time",
        required: true,
        className: "col-span-1 mt-3",
      },
      {
        name: "notes",
        type: "textarea",
        label: "Notes",
        placeholder: "Add any additional notes about the call...",
        rows: 3,
        className: "col-span-2 mt-3",
      },
    ],
    [getPurposeOptions, getStatusOptions]
  );

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    form.reset(formData);
  }, [formData, form]);

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [fieldName]: value,
      };
      
      // Check if all required fields are filled
      const isValid = newData.purpose && newData.status && newData.date && newData.time;
      
      // Notify parent about validation state
      if (onValidationChange) {
        onValidationChange(!!isValid);
      }
      
      // If form is complete, notify parent with call data
      if (isValid && onCallDataChange) {
        const callData: CallEntryData = {
          consumer: Number(entityId) || 0,
          call_purpose: Number(newData.purpose),
          call_status: Number(newData.status),
          call_date: newData.date,
          call_time: newData.time,
          notes: newData.notes,
        };
        onCallDataChange(callData);
      }
      
      return newData;
    });
  }, [entityId, onCallDataChange, onValidationChange]);

  const submitCallEntry = useCallback(async (): Promise<CallEntryData> => {
    console.log('communication form data',formData);
    const callData: CallEntryData = {
      consumer: Number(entityId) || 0,
      call_purpose: Number(formData.purpose),
      call_status: Number(formData.status),
      call_date: formData.date,
      call_time: formData.time,
      notes: formData.notes,
    };

    await createCallEntry(callData);
    return callData;
  }, [formData, entityId, createCallEntry]);

  // Expose submitCallEntry method to parent
  useImperativeHandle(ref, () => ({
    submitCallEntry,
  }));

  const handleSubmit = async () => {
    try {
      await submitCallEntry();
      handleReset();
    } catch (error) {
      console.error("Failed to submit call entry:", error);
      throw error;
    }
  };

  const handleReset = useCallback(() => {
    setFormData(initialFormData);
    form.reset(initialFormData);
    if (onValidationChange) {
      onValidationChange(false);
    }
    if (onCancel) {
      onCancel();
    }
  }, [form, onCancel, onValidationChange]);

  const selectedScript = useMemo(() => {
    return formData.purpose
      ? callScripts[formData.purpose as keyof typeof callScripts]
      : null;
  }, [formData.purpose]);

  return (
    <div className="border border-gray-200 rounded-lg p-6 mt-4 bg-gray-50">
      <div className="flex items-center gap-2 mb-4">
        <Phone className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Call Entry</h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left side - Form */}
        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={showSubmitButton ? handleSubmit : undefined}
            onReset={showSubmitButton ? handleReset : undefined}
            onFieldChange={handleFieldChange}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: showSubmitButton,
              showSubmitButton: showSubmitButton,
              submitButtonText: "Add Call Entry",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                form: "space-y-6",
                button: "px-4 py-2 rounded-md font-medium transition-colors",
                buttonContainer:
                  "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                label: "block text-sm font-medium text-gray-700 mb-1",
                error: "text-sm text-red-600 mt-1",
                container: "space-y-1",
              },
            }}
          />
        </div>

        {/* Right side - Script */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ScrollText className="h-4 w-4" />
                Call Script
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedScript ? (
                <div className="p-4 bg-muted rounded-md mt-3">
                  <p className="text-sm leading-relaxed">{selectedScript}</p>
                </div>
              ) : (
                <div className="p-4 text-center text-muted-foreground">
                  <ScrollText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    Select a call purpose to view the suggested script
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
});